/**
 * ============================================================================
 * AQUORIX API -
 * ============================================================================
 * File:        src/components/TopNav.tsx
 * Purpose:     Top nav (brand + actions).
 * Version:     v1.0.9
 * Created:     2025-12-29
 * Updated:     2026-07-27
 * Author:      Larry McLean + AI Team
 * Project:     AQUORIX™ API Project
 *
 * Description:
 * - Top navigation bar for dashboard shell
 * - Displays brand identity (operator > affiliate > user display)
 * - Uses /api/v1/me as the single boot authority (canonical keys)
 *
 * Phase 6:
 * - Optional “Switch Operator” action (shown only when parent indicates multi-affiliation)
 * - Does NOT fetch /me when meOverride is provided (locked rule)
 *
 * Change Log:
 * -----------
 * v1.0.1  - 2026-01-07 - Larry McLean & ChatGPT
 *   - Refactor to canonical /api/v1/me keys (user.display_name, user.avatar_url, operator.name/logo_url)
 *   - Remove direct Supabase session fetch from TopNav (uses getMe via api helper)
 *   - Keep branding precedence: operator > user
 * v1.0.2  - 2026-01-07 - Larry McLean & ChatGPT
 *   - Fix header File path to match actual location (src/components/TopNav.tsx)
 *   - Add optional prop override to avoid duplicate /me fetch (Layout can pass me)
 *   - Keep safe fallback: fetch /me only if override not provided
 * v1.0.3  - 2026-01-13 - Larry McLean + AI Team
 *   - FIX: Remove getMe/getMeJson mismatch (TopNav now imports and uses getMe consistently)
 *   - FIX: Ensure TopNav never fetches when meOverride is provided
 * v1.0.4  - 2026-02-17 - Larry McLean + AI Team
 *   - Phase 6: Add optional “Switch Operator” button (parent-controlled)
 *
 * v1.0.5  - 2026-07-27 - Larry McLean + AI Team
 * - C1: Remove the obsolete Brand eyebrow from the operator identity.
 *
 * v1.0.6  - 2026-07-27 - Larry McLean + AI Team
 *   - C1: Add the account menu, identity details, and close behavior.
 *   - C2: Clear the authentication session and full-load /login on sign-out.
 *
 * v1.0.7  - 2026-07-27 - Larry McLean + AI Team
 * - C2: Use local sign-out with a 1500 ms ceiling and replace the dashboard history entry.
 * 
 * v1.0.8  - 2026-08-11 - Larry McLean / Claude
 * - Wire the Help (?) button to open the HelpDrawer slide-over (dirt-simple Viking help).
 * 
 * v1.0.9  - 2026-08-11 - Larry McLean / Claude
 * - Add language button in upper top NAV section right justified.
 *
 * Notes:
 * - Do NOT alter existing changelog entries
 * - Always append new changes with version bump
 * - Follow semantic versioning: MAJOR.MINOR.PATCH
 * ============================================================================
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Bell, Globe, HelpCircle, User as UserIcon } from "lucide-react";
import { supabase } from "../lib/supabaseClient";
import { getMe } from "../utils/api";
import HelpDrawer from "./HelpDrawer";

/**
 * LANGUAGES — single source of truth for the language switcher.
 *
 * TO ADD A LANGUAGE: add one line to this array.
 *   code    = BCP-47 code ("en", "ar", "fr", "es", …)
 *   endonym = the language's name IN ITS OWN SCRIPT
 *             (English -> "English", Arabic -> "العربية").
 *             Never translate this into English — people look for
 *             their own language written the way they write it.
 *   dir     = "ltr" for most; "rtl" for Arabic / Hebrew / Farsi.
 *
 * ARABIC is scaffolded below, commented out. When the translations are
 * ready, delete the two leading slashes to turn it on.
 *
 * The real switch (active language + document direction) happens in
 * handleSelectLanguage() — wire your i18n library there when you add one.
 */
type LanguageOption = { code: string; endonym: string; dir: "ltr" | "rtl" };

const LANGUAGES: LanguageOption[] = [
  { code: "en", endonym: "English", dir: "ltr" },
  // { code: "ar", endonym: "العربية", dir: "rtl" },   // ← Arabic: remove the leading // when ready
];

export type MeResponse = {
  api_version?: string;
  ok: boolean;
  authenticated: boolean;
  routing_hint?: "login" | "onboarding" | "dashboard" | string;
  ui_mode?: "admin" | "pro" | "affiliate" | string;

  user?: {
    user_id?: number;
    email?: string;
    tier_level?: number;
    display_name?: string | null;
    avatar_url?: string | null;
  };

  permissions?: Record<string, boolean>;

  operator?: {
    operator_id?: number | string | null;
    name?: string | null;
    logo_url?: string | null;
    location_city?: string | null;
    location_region?: string | null;
  };

  affiliate?: {
    affiliate_id?: number | string | null;
    name?: string | null;
    logo_url?: string | null;
  };
};

type TopNavProps = {
  /**
   * Optional: Provide /api/v1/me payload from a parent (Layout/Provider)
   * to avoid duplicate /me fetches.
   */
  meOverride?: MeResponse | null;

  /**
   * Optional: If parent is fetching /me, pass loading state for skeleton UI.
   */
  loadingOverride?: boolean;

  /**
   * Phase 6: Parent can show "Switch Operator" if user has >1 affiliation.
   */
  showSwitchOperator?: boolean;

  /**
   * Phase 6: Called when user clicks "Switch Operator".
   * Parent should open OperatorSelectorModal.
   */
  onSwitchOperator?: () => void;
};

function safeInitials(displayName?: string | null, email?: string | null) {
  const name = (displayName || "").trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return `${parts[0][0]}`.toUpperCase();
  }
  const e = (email || "").trim();
  if (e) return e[0].toUpperCase();
  return "A";
}

const TopNav: React.FC<TopNavProps> = ({
  meOverride,
  loadingOverride,
  showSwitchOperator,
  onSwitchOperator
}) => {
  const [meLocal, setMeLocal] = useState<MeResponse | null>(null);
  const [loadingLocal, setLoadingLocal] = useState<boolean>(true);
  const [menuOpen, setMenuOpen] = useState(false);

    // Language switcher — mirrors the account-menu pattern above.
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [langCode, setLangCode] = useState("en"); // active language; default English
  const [helpOpen, setHelpOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement | null>(null);

  const me = meOverride !== undefined ? meOverride : meLocal;
  const loading = loadingOverride !== undefined ? loadingOverride : loadingLocal;

  useEffect(() => {
    // If parent provides me (even null), do not fetch here.
    if (meOverride !== undefined) return;

    let mounted = true;

    async function loadMe() {
      setLoadingLocal(true);
      try {
        const res = await getMe();
        if (!res.ok) throw new Error(`getMe HTTP ${res.status}`);
        const data = (await res.json()) as MeResponse;

        if (mounted) setMeLocal(data);
      } catch (err) {
        console.error("[TopNav] getMe failed:", err);
        if (mounted) setMeLocal(null);
      } finally {
        if (mounted) setLoadingLocal(false);
      }
    }

    void loadMe();

    return () => {
      mounted = false;
    };
  }, [meOverride]);

  const brand = useMemo(() => {
    const userEmail = me?.user?.email ?? null;
    const userDisplay = me?.user?.display_name ?? null;
    const userAvatar = me?.user?.avatar_url ?? null;

    const operatorName = me?.operator?.name ?? null;
    const operatorLogo = me?.operator?.logo_url ?? null;

    const affiliateName = me?.affiliate?.name ?? null;
    const affiliateLogo = me?.affiliate?.logo_url ?? null;

    const displayName =
      operatorName || affiliateName || userDisplay || userEmail || "AQUORIX Pro";

    const logoUrl = operatorLogo || affiliateLogo || userAvatar || null;

    const initials = safeInitials(userDisplay || displayName, userEmail);

    const location = [me?.operator?.location_city, me?.operator?.location_region]
      .filter(Boolean)
      .join(" · ");

    return { displayName, logoUrl, initials, location };
  }, [me]);

  const account = useMemo(() => {
    const personName = me?.user?.display_name ?? null;
    const personEmail = me?.user?.email ?? null;
    const operatorName = me?.operator?.name ?? null;

    return { personName, personEmail, operatorName };
  }, [me]);

  const canShowSwitch = Boolean(showSwitchOperator && onSwitchOperator);

  useEffect(() => {
    if (!menuOpen && !langMenuOpen) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") { setMenuOpen(false); setLangMenuOpen(false);}
    }

    function handlePointerDown(event: MouseEvent) {
      if (
        accountMenuRef.current &&
        !accountMenuRef.current.contains(event.target as Node)
      ) {
        setMenuOpen(false);
        setLangMenuOpen(false);
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handlePointerDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, [menuOpen, langMenuOpen]);

  async function handleSignOut() {
    if (signingOut) return;

    setSigningOut(true);

    try {
      await Promise.race([
        supabase.auth.signOut({ scope: "local" }),
        new Promise<void>((resolve) => {
          window.setTimeout(resolve, 1500);
        }),
      ]);
    } finally {
      window.location.replace("/login");
    }
  }

    const currentLanguage =
    LANGUAGES.find((l) => l.code === langCode) || LANGUAGES[0];

  const handleSelectLanguage = (code: string) => {
    const lang = LANGUAGES.find((l) => l.code === code);
    if (!lang) return;
    setLangCode(lang.code);
    setLangMenuOpen(false);
    // Flip the whole document for right-to-left languages (Arabic, Hebrew, Farsi…).
    document.documentElement.dir = lang.dir;
    document.documentElement.lang = lang.code;
    // TODO (i18n): when a translation library is wired in, trigger it here, e.g.
    //   i18n.changeLanguage(lang.code);
  };

  return (
    <>
      <div className="aqx-topnav-left">
        <div className="aqx-topnav-logo" aria-label="Brand logo">
          {loading ? (
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 10,
                background: "rgba(255,255,255,0.10)"
              }}
            />
          ) : brand.logoUrl ? (
            <img src={brand.logoUrl} alt={`${brand.displayName} logo`} />
          ) : (
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                background: "rgba(255,255,255,0.10)"
              }}
              title="No logo (text brand)"
            >
              {brand.initials}
            </div>
          )}
        </div>

        <div className="aqx-topnav-operator-block">
          <div className="aqx-topnav-operator-name">
            {loading ? "AQUORIX" : brand.displayName}
          </div>
          {!loading && brand.location ? (
            <div className="aqx-topnav-operator-loc">{brand.location}</div>
          ) : null}
        </div>
      </div>

      <div className="aqx-topnav-actions" ref={accountMenuRef}>
        {canShowSwitch ? (
          <button
            className="aqx-btn-primary"
            type="button"
            onClick={() => onSwitchOperator?.()}
            title="Switch active operator"
          >
            <span>Switch Operator</span>
          </button>
        ) : null}

        <button className="aqx-btn-icon" aria-label="Notifications" type="button">
          <Bell />
        </button>
        <button className="aqx-btn-icon" aria-label="Help" type="button" onClick={() => setHelpOpen(true)}>
          <HelpCircle />
        </button>
        <button
          className="aqx-btn-primary"
          type="button"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <UserIcon />
          <span>Profile</span>
        </button>
        {menuOpen ? (
          <div className="aqx-topnav-menu" role="menu" aria-label="Account">
            <div className="aqx-topnav-menu-label">Signed in as</div>
            {account.personName ? (
              <div className="aqx-topnav-menu-name">{account.personName}</div>
            ) : null}
            <div className="aqx-topnav-menu-email">
              {account.personEmail || "No email on file"}
            </div>

            {account.operatorName ? (
              <div className="aqx-topnav-menu-block">
                <div className="aqx-topnav-menu-label">Operator</div>
                <div className="aqx-topnav-menu-operator">{account.operatorName}</div>
              </div>
            ) : null}

            <div className="aqx-topnav-menu-divider" />

            <button
              className="aqx-topnav-menu-item"
              type="button"
              role="menuitem"
              onClick={handleSignOut}
              disabled={signingOut}
            >
              {signingOut ? "Signing out…" : "Sign out"}
            </button>
          </div>
        ) : null}

                {/* Language switcher — looks like the Profile button; globe = neutral, language-agnostic. */}
        <button
          className="aqx-btn-primary"
          type="button"
          aria-haspopup="menu"
          aria-expanded={langMenuOpen}
          aria-label="Change language"
          onClick={() => setLangMenuOpen((open) => !open)}
        >
          <Globe />
          <span>{currentLanguage.endonym}</span>
        </button>
        {langMenuOpen ? (
          <div className="aqx-topnav-menu aqx-lang-menu" role="menu" aria-label="Language">
            <div className="aqx-topnav-menu-label">Language</div>
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                className={`aqx-lang-item${lang.code === langCode ? " is-active" : ""}`}
                type="button"
                role="menuitemradio"
                aria-checked={lang.code === langCode}
                lang={lang.code}
                dir={lang.dir}
                onClick={() => handleSelectLanguage(lang.code)}
              >
                <span className="aqx-lang-endonym">{lang.endonym}</span>
                {lang.code === langCode ? <span className="aqx-lang-check">✓</span> : null}
              </button>
            ))}
          </div>
        ) : null}

      </div>

      <HelpDrawer open={helpOpen} onClose={() => setHelpOpen(false)} />
    </>
  );
};

export default TopNav;
